import { useMemo, useState } from "react";
import type {
  ScorecardBrand,
  ScorecardBrandMapping,
  ScorecardResponseCategory,
} from "@models/Scorecard";
import { autoMatchBrands } from "@utils/scorecard-utils";

/**
 * Owns a row's own brand-mapping resolution: auto-match plus the
 * researcher's manual-bind / mark-unavailable overrides. Extracted from
 * QuestionRowBuilder.tsx to keep it under the file-size limit — reused as-is
 * for a New Question Base's own independent mapping and for
 * DatasetMetricRowBuilder's mapping (same shape in all three cases).
 */
export function useRowBrandMapping(
  brands: ScorecardBrand[],
  responseCategories: ScorecardResponseCategory[],
  editingRow?: { brandMappings: ScorecardBrandMapping[] } | null,
) {
  const [manualBindings, setManualBindings] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    editingRow?.brandMappings.forEach((m) => {
      if (m.status === "manual" && m.boundResponseId) initial[m.brandId] = m.boundResponseId;
    });
    return initial;
  });
  const [unavailableBrands, setUnavailableBrands] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    editingRow?.brandMappings.forEach((m) => {
      if (m.status === "unavailable") initial[m.brandId] = m.unavailableReason ?? "";
    });
    return initial;
  });

  const mappings: ScorecardBrandMapping[] = useMemo(() => {
    const auto = autoMatchBrands(brands, responseCategories);
    return auto.map((m): ScorecardBrandMapping => {
      if (m.brandId in unavailableBrands) {
        return {
          brandId: m.brandId,
          status: "unavailable",
          boundResponseId: null,
          boundResponseLabel: null,
          unavailableReason: unavailableBrands[m.brandId] || null,
        };
      }
      const manualId = manualBindings[m.brandId];
      if (!manualId) return m;
      const category = responseCategories.find((c) => c.id === manualId);
      // The bound response no longer exists among the current candidates
      // (e.g. the underlying metric/question definition changed) — fall
      // back to unresolved rather than silently keeping a dangling
      // "manual" mapping that points at nothing.
      if (!category) {
        return {
          brandId: m.brandId,
          status: "unresolved",
          boundResponseId: null,
          boundResponseLabel: null,
        };
      }
      return {
        brandId: m.brandId,
        status: "manual",
        boundResponseId: manualId,
        boundResponseLabel: category.label,
        matchLabels: category.matchLabels,
      };
    });
  }, [brands, responseCategories, manualBindings, unavailableBrands]);

  const resetForNewQuestion = () => {
    setManualBindings({});
    setUnavailableBrands({});
  };

  const onManualBind = (brandId: string, responseId: string) =>
    setManualBindings((prev) => ({ ...prev, [brandId]: responseId }));

  const onMarkUnavailable = (brandId: string, reason: string) => {
    setUnavailableBrands((prev) => ({ ...prev, [brandId]: reason }));
    setManualBindings((prev) => {
      const next = { ...prev };
      delete next[brandId];
      return next;
    });
  };

  const onRestoreMapping = (brandId: string) => {
    setUnavailableBrands((prev) => {
      const next = { ...prev };
      delete next[brandId];
      return next;
    });
  };

  return { mappings, resetForNewQuestion, onManualBind, onMarkUnavailable, onRestoreMapping };
}
